/**
 * API Client
 * Axios instance with Keycloak authentication and error handling
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { keycloak, refreshToken, getTenantId } from '../auth/keycloak'
import { API_CONFIG, BACKEND_MODE, resolveApiUrl } from './config'
import { friendlyError } from '../friendly-error'

// Create Axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseUrl,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Request interceptor - resolve every non-absolute URL via resolveApiUrl.
// Runs in cloud-direct AND cloud-gateway: resolveApiUrl handles version-prefix
// doubling for already-versioned /api/v2/* paths (otherwise the axios baseURL,
// which ends in /api/v1, would produce /api/v1/api/v2/... → 404).
if (BACKEND_MODE === 'cloud-direct' || BACKEND_MODE === 'cloud-gateway') {
  apiClient.interceptors.request.use(
    (config) => {
      if (config.url && !config.url.startsWith('http')) {
        config.url = resolveApiUrl(config.url)
        config.baseURL = '' // Clear baseURL so resolved absolute URL is used
      }
      return config
    },
    (error) => Promise.reject(error)
  )
}

// Request interceptor - Add auth token and tenant ID
apiClient.interceptors.request.use(
  async (config) => {
    // Refresh token if needed (30 seconds before expiry)
    if (keycloak.authenticated) {
      await refreshToken(30)

      // Add Authorization header
      if (keycloak.token) {
        config.headers.Authorization = `Bearer ${keycloak.token}`
      }

      // Add tenant ID header
      const tenantId = getTenantId()
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId
      }
    }

    // Add correlation ID for tracing
    config.headers['X-Correlation-ID'] = crypto.randomUUID()

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Single-flight refresh promise for the 401 interceptor. Even though
// keycloak.updateToken() is itself dedupe-wrapped (see lib/auth/keycloak.ts),
// we still want one shared awaitable here so that N concurrent 401s share a
// single refresh AND retry off the same resolved token in lock-step.
let refreshInFlight: Promise<boolean> | null = null

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    // Handle 401 - Try to refresh token (single-flight)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        if (!refreshInFlight) {
          refreshInFlight = refreshToken(0).finally(() => {
            refreshInFlight = null
          })
        }
        const refreshed = await refreshInFlight
        if (refreshed && keycloak.token) {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${keycloak.token}`,
          }
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // Redirect to login if refresh fails
        keycloak.login()
        return Promise.reject(refreshError)
      }
    }

    // Handle 403 - Forbidden
    // S28: downgrade to warn so UI graceful-block handlers can show "Accès refusé"
    // without triggering console.error (which violates the zero-console-error DoD).
    if (error.response?.status === 403) {
      console.warn('[api] 403 Forbidden:', error.config?.url, error.response.data)
    }

    // Handle 404 - Not found
    // S28: downgrade to warn to keep zero-console-error DoD on missing optional resources
    if (error.response?.status === 404) {
      console.warn('[api] 404 Not found:', error.config?.url)
    }

    // Handle 500+ - Server errors
    // #DA-raw502: a raw nginx 502/504 returns an HTML body (<html>…502…</html>).
    // Never log that (or a stack) verbatim — route it through friendlyError() so
    // only a short, safe FR message is emitted. Downgraded to warn to keep the
    // zero-console-error DoD (consistent with the 403/404 handling above).
    if (error.response?.status && error.response.status >= 500) {
      console.warn('[api] Server error:', error.config?.url, friendlyError(error))
    }

    return Promise.reject(error)
  }
)

// API response types
export interface ApiResponse<T> {
  data: T
  message?: string
  status: number
}

export interface ApiError {
  message: string
  code?: string
  details?: Record<string, unknown>
  status: number
}

export interface PaginatedResponse<T> {
  items: T[]
  /** Alias for items (some pages destructure .data) */
  data?: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface PaginationParams {
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

// Generic request methods
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<T>(url, config)
  return response.data
}

export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config)
  return response.data
}

export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<T>(url, data, config)
  return response.data
}

export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config)
  return response.data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<T>(url, config)
  return response.data
}

// File upload helper
export async function uploadFile<T>(
  url: string,
  file: File,
  onProgress?: (progress: number) => void,
  fields?: Record<string, string | undefined | null>
): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)
  // Append extra multipart Form fields (e.g. evidence-service requires a `title`
  // Form field — sending only the file returns HTTP 422).
  if (fields) {
    for (const [k, v] of Object.entries(fields)) {
      if (v !== undefined && v !== null && v !== '') formData.append(k, String(v))
    }
  }

  const response = await apiClient.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    },
  })

  return response.data
}

// File download helper
export async function downloadFile(url: string, filename: string): Promise<void> {
  const response = await apiClient.get(url, {
    responseType: 'blob',
  })

  const blob = new Blob([response.data])
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = filename
  link.click()
  window.URL.revokeObjectURL(link.href)
}

// Create service-specific client
export function createServiceClient(serviceBaseUrl: string): AxiosInstance {
  const serviceClient = axios.create({
    baseURL: serviceBaseUrl,
    timeout: API_CONFIG.timeout,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })

  // Copy interceptors from main client
  serviceClient.interceptors.request.use(
    apiClient.interceptors.request.handlers[0].fulfilled,
    apiClient.interceptors.request.handlers[0].rejected
  )

  serviceClient.interceptors.response.use(
    apiClient.interceptors.response.handlers[0].fulfilled,
    apiClient.interceptors.response.handlers[0].rejected
  )

  return serviceClient
}

export default apiClient
